import { NgClass } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  HostBinding,
  OnInit,
  viewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  SessionStorageService,
  FeatureService,
  GlobalStore,
} from '@maximizer/core/shared/data-access';
import { InsightsService } from '@maximizer/core/shared/insights';
import {
  FormGroupWrapper,
  FormWrapperBuilder,
} from '@maximizer/core/shared/ui/forms';
import { UI_LOADER } from '@maximizer/core/shared/ui/components/loader';
import { UI_KENDO } from '@maximizer/core/shared/ui/kendo';
import { StepGuideComponent } from '@maximizer/outlook/onboarding/ui';
import {
  LeadService,
  NavigationService,
  OnboardingService,
  OnboardingStepNumber,
  OnboardingStore,
  OutlookService,
  StepGuideId,
  TrackerService,
  EmailSignatureAiService,
  OutlookInsightsEvents,
  OutlookStore,
  EmailSignatureFields,
  AI_EXTRACTION_MIN_BODY_LENGTH,
} from '@maximizer/outlook/shared/data-access';
import {
  EventAction,
  EventJourney,
  EventMetadata,
  EventModule,
  OutlookLeadForm,
} from '@maximizer/outlook/shared/domain';
import {
  ONBOARDING_STEP_POPOVER_OPEN_DELAY,
  ONBOARDING_STEP_POPOVER_WIDTH,
  OUTLOOK_SHARED_UI,
  OutlookNotificationComponent,
  PHONE_VALIDATORS,
} from '@maximizer/outlook/shared/ui';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { TranslatePipe } from '@ngx-translate/core';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { KENDO_INPUTS } from '@progress/kendo-angular-inputs';
import { KENDO_LABEL } from '@progress/kendo-angular-label';
import {
  KENDO_TOOLTIPS,
  PopoverContainerDirective,
} from '@progress/kendo-angular-tooltip';
import {
  catchError,
  finalize,
  firstValueFrom,
  forkJoin,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { ListItem } from '@maximizer/core/shared/domain';
import { AiFeaturesService } from '@maximizer/ai/data-access';

@Component({
  selector: 'maximizer-add-lead',
  templateUrl: './add-lead.component.html',
  imports: [
    FormsModule,
    KENDO_BUTTON,
    KENDO_DROPDOWNS,
    KENDO_INPUTS,
    KENDO_LABEL,
    KENDO_TOOLTIPS,
    NgClass,
    OUTLOOK_SHARED_UI,
    ReactiveFormsModule,
    StepGuideComponent,
    TranslatePipe,
    UI_KENDO,
    UI_LOADER,
  ],
})
export class AddLeadComponent implements OnInit, AfterViewInit {
  @HostBinding('class.max-outlook') hostClass = true;

  notification =
    viewChild.required<OutlookNotificationComponent>('notification');
  addContact2Container = viewChild<PopoverContainerDirective>(
    'addContact2Container',
  );
  addContact2Anchor = viewChild<Element>('addContact2Anchor');
  pageContainer =
    viewChild.required<ElementRef<HTMLDivElement>>('pageContainer');

  private readonly newPartitionKey = 'new-item';
  readonly stepPopoverWidth = ONBOARDING_STEP_POPOVER_WIDTH;

  leadKey = '';
  loading = true;
  aiLoading = false;
  addPhone2 = false;
  countries: ListItem<string>[] = [];
  leadData?: OutlookLeadForm;
  form?: FormGroupWrapper<OutlookLeadForm>;
  StepGuideId = StepGuideId;

  onboardingGuide = computed(() => this.onboardingStore.inProgress());

  // AI feature flags and settings
  aiEnabled = false;
  aiAdminOutlookEnabled = false;
  aiUserOutlookEnabled = false;
  aiOutlookFeatureFlagEnabled = false;
  aiPrefillFeatureFlagEnabled = false;

  aiPrefillSettingsEnabled = computed(
    () =>
      this.aiEnabled &&
      this.aiAdminOutlookEnabled &&
      this.aiUserOutlookEnabled &&
      this.aiOutlookFeatureFlagEnabled &&
      this.aiPrefillFeatureFlagEnabled,
  );

  constructor(
    private readonly leadService: LeadService,
    private readonly route: ActivatedRoute,
    private readonly storage: SessionStorageService,
    private readonly outlookService: OutlookService,
    private readonly navigationService: NavigationService,
    private readonly tracker: TrackerService,
    private readonly signatureAi: EmailSignatureAiService,
    private readonly insightsService: InsightsService,
    private readonly feature: FeatureService,
    private readonly aiFeaturesService: AiFeaturesService,
    public onboardingService: OnboardingService,
    public onboardingStore: OnboardingStore,
    public outlookStore: OutlookStore,
    public globalStore: GlobalStore,
  ) {
    effect(() => {
      const inProgress = this.onboardingGuide();
      const container = this.addContact2Container();
      const anchor = this.addContact2Anchor();

      if (inProgress && container && anchor) {
        container.show(anchor);
      } else if (!inProgress) {
        container?.hide();
      }
    });

    this.aiOutlookFeatureFlagEnabled = this.feature.isFeatureOn(
      'integration-o365-ai-features-enabled',
      false,
    );

    this.aiPrefillFeatureFlagEnabled = this.feature.isFeatureOn(
      'integration-o365-ai-prefill-entry-shown',
      false,
    );
  }

  ngOnInit(): void {
    this.loadData();
  }

  ngAfterViewInit(): void {
    if (this.onboardingStore.inProgress()) {
      this.showOnboardingGuide();
    }
  }

  private loadData(): void {
    this.loading = true;

    const requests = {
      fieldOptions: this.leadService.getFieldOption(),
      aiEnabled: this.aiFeaturesService.getEnabled(),
    };

    forkJoin(requests)
      .pipe(
        tap((result) => {
          this.aiEnabled = result?.aiEnabled ?? false;
          if (result?.fieldOptions) {
            this.countries = result.fieldOptions.addressCountry;
          }
        }),
        switchMap((result) =>
          result?.aiEnabled ? this.getAIFeatureSettings() : of(null),
        ),
        catchError(() => {
          this.notification().show('load', 'error');
          return of(null);
        }),
        finalize(() => (this.loading = false)),
      )
      .subscribe(() => {
        this.setForm();
      });
  }

  private getAIFeatureSettings() {
    const session = this.globalStore.session();
    if (!session) {
      return of(null);
    }

    return this.aiFeaturesService.getAIUserData(session).pipe(
      tap((result) => {
        if (result) {
          this.aiAdminOutlookEnabled = result.admin.aiFeaturesEnabled.outlook;
          this.aiUserOutlookEnabled = result.user.aiFeaturesEnabled.outlook;
        }
      }),
      catchError(() => of(null)),
    );
  }

  private setForm(): void {
    const email = this.route.snapshot.queryParamMap.get('email');
    const name = this.route.snapshot.queryParamMap.get('name');
    let firstName = '';
    let lastName = '';

    if (name) {
      const fullName = name.split(' ');
      if (fullName.length === 1) {
        lastName = fullName[0];
      } else {
        firstName = fullName[0] ?? '';
        lastName = fullName[1] ?? '';
      }
    }

    this.form = FormWrapperBuilder.group<OutlookLeadForm>({
      key: null,
      firstName: [firstName, [Validators.maxLength(79)]],
      lastName: [lastName, [Validators.required, Validators.maxLength(79)]],
      email: [email, []],
      address1: [null, [Validators.maxLength(79)]],
      address2: [null, [Validators.maxLength(79)]],
      phone1: [null, PHONE_VALIDATORS],
      phone2: [null, PHONE_VALIDATORS],
      company: [null, [Validators.maxLength(79)]],
      position: [null, [Validators.maxLength(79)]],
      website: [null, [Validators.maxLength(255)]],
      city: [null, [Validators.maxLength(79)]],
      province: [null, [Validators.maxLength(79)]],
      postalCode: [null, [Validators.maxLength(79)]],
      country: [null, [Validators.maxLength(79)]],
    });

    if (this.aiPrefillSettingsEnabled()) {
      this.initializeAiPrefill();
    }
  }

  private async initializeAiPrefill(): Promise<void> {
    if (
      this.outlookStore.isSentFolder() ||
      this.outlookService.isCalendarItem
    ) {
      return;
    }

    try {
      const body = await firstValueFrom(
        this.outlookService.getMessageBodyText(),
      );

      if (!body || body.length < AI_EXTRACTION_MIN_BODY_LENGTH) {
        return;
      }

      const newContactEmail = this.form?.value.email ?? '';
      this.processSignatureData(newContactEmail, body);
    } catch (err) {
      console.error('[AddLead] Prefill failed:', err);
      this.aiLoading = false;
    }
  }

  private processSignatureData(newContactEmail: string, body: string) {
    const firstName = this.form?.value.firstName?.trim() ?? '';
    const lastName = this.form?.value.lastName?.trim() ?? '';
    const fullName = `${firstName} ${lastName}`.trim();

    this.aiLoading = true;

    this.signatureAi
      .extract(newContactEmail, fullName, body)
      .pipe(
        catchError((error) => {
          this.insightsService.trackEvent(
            { name: 'add-lead-ai-extraction-failed' },
            SeverityLevel.Error,
            {
              error: error.message,
              eventId: OutlookInsightsEvents.AiAutoPopulate,
            },
          );

          return of(null);
        }),
        finalize(() => (this.aiLoading = false)),
      )
      .subscribe((aiResult) => {
        if (!aiResult || !this.form) {
          return;
        }

        const patch: Partial<OutlookLeadForm> = {};

        this.patchAiPrefillNameFields(aiResult, patch);

        if (aiResult.company) patch.company = aiResult.company;
        if (aiResult.position) patch.position = aiResult.position;
        if (aiResult.website) patch.website = aiResult.website;
        if (aiResult.address) patch.address1 = aiResult.address;
        if (aiResult.phones?.[0]) patch.phone1 = aiResult.phones[0];
        if (aiResult.phones?.[1]) patch.phone2 = aiResult.phones[1];
        this.form?.patch(patch);
      });
  }

  private patchAiPrefillNameFields(
    aiResult: EmailSignatureFields,
    patch: Partial<OutlookLeadForm>,
  ): void {
    // Only populate name fields if both are empty
    const currentFirstName = this.form?.value.firstName?.trim();
    const currentLastName = this.form?.value.lastName?.trim();

    if (!currentFirstName && !currentLastName) {
      if (aiResult.firstName) patch.firstName = aiResult.firstName;
      if (aiResult.lastName) patch.lastName = aiResult.lastName;
    }
  }

  save(): void {
    if (!this.form) return;

    const validation = this.form.validate();
    if (!validation.valid) return;

    this.loading = true;

    this.leadService
      .save(this.form.value)
      .pipe(
        catchError(async () => {
          this.notification().show('create', 'error');
          return null;
        }),
        finalize(() => {
          this.loading = false;

          if (this.onboardingStore.inProgress()) {
            this.nextOnboardingStep(OnboardingStepNumber.Step1);
          }
        }),
      )
      .subscribe((result) => {
        const success = !!result;

        this.tracker.trackEvent({
          module: EventModule.Entry,
          journey: EventJourney.Create,
          action: EventAction.Completed,
          metadata: {
            entry: 'lead',
            result: success ? EventMetadata.Success : EventMetadata.Failed,
          },
        });

        if (!result) return;
        const existing = this.storage.get(
          this.outlookService.storagePartitionKey + this.newPartitionKey,
        );
        let entryArray: string[] = [];
        if (existing) {
          entryArray = JSON.parse(existing);
        }
        if (this.form) {
          entryArray.push(this.form.value.email);

          this.storage.set(
            this.outlookService.storagePartitionKey + this.newPartitionKey,
            JSON.stringify(entryArray),
          );
        }

        this.notification().show('create', 'success');
        this.navigationService.navigateBack();
      });
  }

  onRemovePhone(): void {
    this.form?.patch({ phone2: '' });
    this.addPhone2 = false;
  }

  cancel(): void {
    if (this.onboardingStore.inProgress()) {
      this.skipOnboarding();
      return;
    }
    this.navigationService.navigateBack();
  }

  onFormFocusIn(): void {
    if (
      this.onboardingStore.inProgress() &&
      this.onboardingStore.currentGuide() === StepGuideId.AddContact2
    ) {
      this.addContact2Container()?.hide();
    }
  }

  onFormFocusOut(event: FocusEvent): void {
    if (
      this.onboardingStore.inProgress() &&
      this.onboardingStore.currentGuide() === StepGuideId.AddContact2
    ) {
      const container = this.pageContainer().nativeElement;
      if (!container.contains(event.relatedTarget as Node)) {
        this.showAddContact2Popover();
      }
    }
  }

  // Onboarding
  showOnboardingGuide(): void {
    this.onboardingStore.setCurrentGuide(StepGuideId.AddContact2);
    setTimeout(() => {
      this.showAddContact2Popover();
    }, ONBOARDING_STEP_POPOVER_OPEN_DELAY);
  }

  private showAddContact2Popover(): void {
    const anchor = this.addContact2Anchor();
    if (anchor) {
      this.addContact2Container()?.show(anchor);
    }
  }

  skipOnboarding(): void {
    this.nextOnboardingStep(OnboardingStepNumber.Step1);
    this.navigationService.navigateBack();
  }

  nextOnboardingStep(currentStep: OnboardingStepNumber): void {
    if (currentStep < OnboardingStepNumber.Step4) {
      this.onboardingStore.setCurrentStep(currentStep + 1);
    }
  }
}
